//window.onerror = (a,b,c) => {
//    debugger;
//}
import "whatwg-fetch";
import "./polyfill";
import "./publicPath";
import type { DatadogRum, RumInitConfiguration } from "@datadog/browser-rum";
import { createApp } from "vue";
import { createPinia } from "pinia"; //cspell:ignore pinia // todo global
import { useMainStore, TenantData } from "./mainStore";
import { GlobalConfigData } from "@cs/GlobalConfigData";
import { vue3Debounce } from "vue-debounce";
import AppShell from "./AppShell.3.vue";
import { vfmPlugin } from "vue-final-modal";
import { vfmInjectKey } from "./appExportKeys.3";

const tenantDataString = document.getElementById("tenantData")?.innerHTML;
let tenantData: TenantData;
if (tenantDataString == "/*644f65f6-b806-49fe-927a-000a3a40d73e*/" || tenantDataString == null) {
    tenantData = {} as TenantData; // TODO
} else {
    tenantData = JSON.parse(tenantDataString);
}

const globalConfigDataString = document.getElementById("globalConfigData")?.innerHTML;
let globalConfigData: GlobalConfigData;
if (globalConfigDataString == "/*d599d548-2a76-4900-805c-c00672ca77af*/" || globalConfigDataString == null) {
    globalConfigData = {} as GlobalConfigData; // TODO
} else {
    globalConfigData = JSON.parse(globalConfigDataString);
}

type RumInitWindow = Window & {
    DD_RUM?: Partial<DatadogRum>;
};

type DatadogRumConfig = {
    enabled: boolean;
    applicationId?: string;
    clientToken?: string;
    site?: string;
    service?: string;
    instance?: string;
    env?: string;
    version?: string;
    sessionSampleRate?: number;
    sessionReplaySampleRate?: number;
    defaultPrivacyLevel?: RumInitConfiguration["defaultPrivacyLevel"];
};

const toOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== "string") {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const toOptionalNumber = (value: unknown): number | undefined => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    return undefined;
};

const toDefaultPrivacyLevel = (value: unknown): RumInitConfiguration["defaultPrivacyLevel"] => {
    const normalized = toOptionalString(value);
    switch (normalized) {
        case "allow":
        case "mask":
        case "mask-user-input":
            return normalized;
        default:
            return undefined;
    }
};

const readConfigValue = (cfg: Record<string, unknown>, pascalCaseKey: string): unknown => {
    if (cfg[pascalCaseKey] !== undefined) {
        return cfg[pascalCaseKey];
    }
    const camelCaseKey = pascalCaseKey.charAt(0).toLowerCase() + pascalCaseKey.slice(1);
    return cfg[camelCaseKey];
};

const getDatadogRumConfig = (): DatadogRumConfig => {
    const cfg = globalConfigData as unknown as Record<string, unknown>;
    return {
        enabled: readConfigValue(cfg, "DatadogRumEnabled") === true,
        applicationId: toOptionalString(readConfigValue(cfg, "DatadogRumApplicationId")),
        clientToken: toOptionalString(readConfigValue(cfg, "DatadogRumClientToken")),
        site: toOptionalString(readConfigValue(cfg, "DatadogRumSite")),
        service: toOptionalString(readConfigValue(cfg, "DatadogRumService")),
        instance: toOptionalString(readConfigValue(cfg, "DatadogRumInstance")),
        env: toOptionalString(readConfigValue(cfg, "DatadogRumEnv")),
        version: toOptionalString(readConfigValue(cfg, "DatadogRumVersion")),
        sessionSampleRate: toOptionalNumber(readConfigValue(cfg, "DatadogRumSessionSampleRate")),
        sessionReplaySampleRate: toOptionalNumber(readConfigValue(cfg, "DatadogRumSessionReplaySampleRate")),
        defaultPrivacyLevel: toDefaultPrivacyLevel(readConfigValue(cfg, "DatadogRumDefaultPrivacyLevel")),
    };
};

const getRumSitePrefix = (site: string): string | undefined => {
    switch ((site ?? "").toLowerCase()) {
        case "datadoghq.eu":
            return "eu";
        case "us3.datadoghq.com":
            return "us3";
        case "us5.datadoghq.com":
            return "us5";
        case "ap1.datadoghq.com":
            return "ap1";
        case "ddog-gov.com":
            return "us1-fed";
        case "datadoghq.com":
            return "us1";
        default:
            return undefined;
    }
};

const ensureDatadogRumInitialized = async () => {
    const rumConfig = getDatadogRumConfig();
    const applicationId = rumConfig.applicationId;
    const clientToken = rumConfig.clientToken;
    const site = rumConfig.site;
    const service = rumConfig.service;
    if (!rumConfig.enabled || !applicationId || !clientToken || !site || !service) {
        return;
    }

    const rumWindow = window as RumInitWindow;
    const initialize = () => {
        if (!rumWindow.DD_RUM?.init) {
            return;
        }
        if (rumWindow.DD_RUM.getInitConfiguration?.()) {
            return;
        }

        const initConfig: RumInitConfiguration = {
            applicationId,
            clientToken,
            site,
            service,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
        };

        if (rumConfig.env) {
            initConfig.env = rumConfig.env;
        }
        if (rumConfig.version) {
            initConfig.version = rumConfig.version;
        }
        if (rumConfig.sessionSampleRate !== undefined) {
            initConfig.sessionSampleRate = rumConfig.sessionSampleRate;
        }
        if (rumConfig.sessionReplaySampleRate !== undefined) {
            initConfig.sessionReplaySampleRate = rumConfig.sessionReplaySampleRate;
        }
        if (rumConfig.defaultPrivacyLevel) {
            initConfig.defaultPrivacyLevel = rumConfig.defaultPrivacyLevel;
        }

        rumWindow.DD_RUM.init(initConfig);
        if (rumConfig.instance) {
            rumWindow.DD_RUM.setGlobalContextProperty?.("instance", rumConfig.instance);
        }
        rumWindow.DD_RUM.setGlobalContextProperty?.("division", "sf");
        rumWindow.DD_RUM.setGlobalContextProperty?.("product", "engagement-builder");
    };

    if (rumWindow.DD_RUM?.init) {
        initialize();
        return;
    }

    await new Promise<void>((resolve, reject) => {
        const rumSite = rumConfig.site;
        if (!rumSite) {
            resolve();
            return;
        }
        const sitePrefix = getRumSitePrefix(rumSite);
        if (!sitePrefix) {
            resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = `https://www.datadoghq-browser-agent.com/${sitePrefix}/v6/datadog-rum.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Datadog RUM script"));
        document.head.appendChild(script);
    });

    initialize();
};

Array.from(document.getElementsByTagName("link"))
    .filter(i => i.dataset.rel != null)
    .forEach(i => {
        i.rel = i.dataset.rel!;
    });

const pinia = createPinia();
const app = createApp({
    ...AppShell,
});
//@ts-ignore
app.use(pinia);
app.use(
    vfmPlugin({
        key: vfmInjectKey as unknown as string,
    }),
);
app.directive("debounce", vue3Debounce({ trim: true }));

const mainStore = useMainStore();
mainStore.tenantData = tenantData;
mainStore.globalConfigData = globalConfigData;

ensureDatadogRumInitialized().catch(() => {
    // RUM should not block the app from loading.
});
app.mount("#container");

export default app;

////@ts-ignore
//if (module.hot) {
//    // accept actions and mutations as hot modules
//    //@ts-ignore
//    module.hot.accept(["./pages/pageState/module.js"], () => {
//        //@ts-ignore
//        const newPageModule = require("./pages/pageState/module.js").default; // eslint-disable-line @typescript-eslint/no-var-requires
//
//        // swap in the new modules and mutations
//        store.hotUpdate({
//            modules: {
//                page: newPageModule,
//            },
//        });
//    });
//}

//if ("serviceWorker" in navigator) {
//    window.addEventListener("load", () => {
//        navigator.serviceWorker.register(tenantData.urlTemplates.defaultTenant.serviceWorker);
//    });
//}
